import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { TrendUp, CalendarCheck, CurrencyInr, ShieldCheck, Megaphone, Gauge, WhatsappLogo } from "@phosphor-icons/react";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSiteInfo, waLink, track, getAttribution } from "@/lib/site";

const PTYPES = ["Farmhouse", "Resort", "Banquet Hall", "Function Hall"];
const emptyLead = { name: "", phone: "", email: "", property_type: "", location: "", message: "" };

const BENEFITS = [
  [TrendUp, "More visibility", "Reach customers actively searching for venues around Hyderabad."],
  [CalendarCheck, "Online booking requests", "Receive and manage booking requests from one dashboard."],
  [Gauge, "Availability management", "Control your calendar and block dates in a click."],
  [CurrencyInr, "Secure payments", "Advance payments collected securely via Razorpay."],
  [ShieldCheck, "Verified listing", "Build customer confidence with an Avinya-verified profile."],
  [Megaphone, "Professional reach", "A polished, shareable listing page for your property."],
];

export default function ListYourProperty() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const info = useSiteInfo();
  const [lf, setLf] = useState(emptyLead);
  const [sent, setSent] = useState(false);
  const setL = (k) => (e) => setLf({ ...lf, [k]: e?.target ? e.target.value : e });

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lf.name || !lf.phone) return toast.error("Please enter your name and mobile number.");
    try {
      await api.post("/owner-leads", { ...lf, attribution: getAttribution() });
      track("owner_signup", { source: "list_your_property_form", stage: "lead" });
      setSent(true);
      setLf(emptyLead);
      toast.success("Thanks! Our team will reach out to help you list your property.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const cta = () => {
    if (user?.role === "owner") navigate("/owner/properties/new");
    else navigate("/register?role=owner");
  };

  return (
    <>
      <Seo title="List Your Property — Grow Your Bookings" path="/list-your-property" description="List your farmhouse, resort, banquet hall or function hall on Avinya Bookings and reach customers looking to book." />
      <section className="bg-gradient-to-b from-accent/60 to-transparent px-4 py-14 md:px-6">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="font-heading text-3xl font-bold text-stone-900 md:text-5xl">Grow Your Bookings with Avinya.</h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-stone-600 md:text-lg">List your farmhouse, resort, banquet hall or function hall and reach customers looking to book.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button data-testid="lyp-cta" onClick={cta} className="rounded-full bg-primary px-8 py-6 text-base hover:bg-green-900">List Your Property</Button>
            {info.support_whatsapp && (
              <a data-testid="lyp-whatsapp" href={waLink(info.support_whatsapp, "Hi Avinya, I'd like to list my property.")} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_clicked", { source: "list_your_property" })} className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-6 py-3 text-sm font-medium text-stone-700 hover:bg-white">
                <WhatsappLogo size={18} weight="fill" className="text-green-600" /> Talk to Avinya
              </a>
            )}
          </div>
        </div>
      </section>
      <div className="mx-auto max-w-5xl px-4 pb-6 md:px-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map(([Icon, t, d]) => (
            <div key={t} className="rounded-2xl border border-stone-200 bg-white p-5">
              <Icon size={26} weight="duotone" className="text-primary" />
              <p className="mt-2 font-heading font-semibold">{t}</p>
              <p className="mt-1 text-sm text-stone-500">{d}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 rounded-2xl border border-stone-200 bg-white p-6">
          <p className="font-heading text-lg font-semibold">How it works</p>
          <ol className="mt-3 grid gap-3 text-sm text-stone-600 sm:grid-cols-4">
            {["Register as an owner", "Complete your listing", "Avinya reviews & approves", "Start receiving bookings"].map((s, i) => (
              <li key={i} className="rounded-xl bg-accent/50 p-3"><span className="font-heading font-bold text-primary">{i + 1}.</span> {s}</li>
            ))}
          </ol>
          <Button data-testid="lyp-cta-2" onClick={cta} className="mt-5 rounded-full bg-primary px-8 py-6 text-base hover:bg-green-900">List Your Property</Button>
        </div>

        <div id="owner-enquiry" className="mt-8 rounded-2xl border border-stone-200 bg-white p-6">
          <p className="font-heading text-lg font-semibold">Prefer we call you?</p>
          <p className="mt-1 text-sm text-stone-500">Share a few details and our team will help you get listed.</p>
          {sent ? (
            <div data-testid="owner-lead-success" className="mt-4 rounded-xl border border-green-200 bg-green-50 p-5 text-center">
              <p className="font-heading font-semibold text-green-800">Request received 🎉</p>
              <p className="mt-1 text-sm text-green-700">Our team will contact you shortly to help you list your property.</p>
            </div>
          ) : (
            <form onSubmit={submitLead} data-testid="owner-lead-form" className="mt-4 space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div><Label className="text-xs text-stone-500">Name *</Label><Input data-testid="ol-name" value={lf.name} onChange={setL("name")} className="mt-1 rounded-xl" placeholder="Your name" /></div>
                <div><Label className="text-xs text-stone-500">Mobile *</Label><Input data-testid="ol-phone" value={lf.phone} onChange={setL("phone")} className="mt-1 rounded-xl" placeholder="10-digit mobile" /></div>
                <div><Label className="text-xs text-stone-500">Email</Label><Input data-testid="ol-email" value={lf.email} onChange={setL("email")} className="mt-1 rounded-xl" placeholder="you@email.com" /></div>
                <div>
                  <Label className="text-xs text-stone-500">Property type</Label>
                  <Select value={lf.property_type} onValueChange={setL("property_type")}>
                    <SelectTrigger data-testid="ol-type" className="mt-1 rounded-xl"><SelectValue placeholder="Choose" /></SelectTrigger>
                    <SelectContent>{PTYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs text-stone-500">Location</Label><Input data-testid="ol-location" value={lf.location} onChange={setL("location")} className="mt-1 rounded-xl" placeholder="e.g. Moinabad" /></div>
              </div>
              <div><Label className="text-xs text-stone-500">Message</Label><Textarea data-testid="ol-message" value={lf.message} onChange={setL("message")} className="mt-1 rounded-xl" placeholder="Tell us about your property..." /></div>
              <Button data-testid="ol-submit" type="submit" className="w-full rounded-xl bg-primary py-6 text-base hover:bg-green-900">Request a call back</Button>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
